import React, { useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';

import Table from '../../../../Table';
import FormContainer from '../../../FormContainer';
import AddOrEditPageTabs from '../../../AddOrEditPageTabs';
import PaymentsTable from './PaymentsTable';
import DeliveriesTable from './DeliveriesTable';
import ButtonWithConfirmation from '../../../../ButtonWithConfirmation';
import AccelerationPackage from '../Actions/AccelerationPackage';

import useDemandForm from '../../../../../forms/loan/demand';
import LoanService from '../../../../../services/LoanService';
import DemandService from '../../../../../services/DemandService';
import { useUiContext } from '../../../../../context/UiContext';
import useUserRole from '../../../../../hooks/useUserRole';

const tabs = [
  {
    label: 'Payments',
  },
  {
    label: 'Demands',
    activeOn: 'Demand',
  },
];

export default function Demand({ loanId }) {
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);

  const { showToast, showDialogModal } = useUiContext();

  const navigate = useNavigate();

  const { resourceId: id } = useParams();

  const { isLender, isSuperAdmin } = useUserRole();

  const { data: invoices, refetch: refetchInvoices } = useQuery({
    queryKey: ['loanPastDueInvoices', loanId],
    queryFn: () => LoanService.getLoanPastDueInvoices(loanId),
    enabled: !!loanId,
  });

  const { data: demand, refetch } = useQuery({
    queryKey: ['demand', id],
    queryFn: () => DemandService.getDemand(id),
    enabled: !!id,
  });

  // Set selected invoice ids if demand has invoices
  useEffect(() => {
    if (demand?.invoice_id && demand.invoice_id.length) {
      setSelectedInvoiceIds(demand?.invoice_id?.map((invoice) => invoice?.id));
    }
  }, [demand]);

  const { mutate: createDemand, isPending } = useMutation({
    mutationFn: (formValues) =>
      DemandService.createDemand({
        formValues,
        loanId,
        invoiceIds: selectedInvoiceIds,
      }),
    onSuccess: (newDemand) => {
      showToast({
        text: 'Demand created. The letter has not been delivered.',
        type: 'success',
      });
      navigate(`/loans/${loanId}/demands/${newDemand.id}?tab=Demand`);
    },
    onError: (error) => {
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: deleteDemand, isPending: isDeleting } = useMutation({
    mutationFn: () => DemandService.deleteDemand(id),
    onSuccess: () => {
      showToast({
        text: 'Success',
        type: 'success',
      });
      navigate(`/loans/${loanId}?tab=Demands`);
    },
    onError: (error) => {
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { mutate: updateDemand, isPending: isUpdating } = useMutation({
    mutationFn: (formValues) =>
      DemandService.updateDemand({
        formValues: {
          ...demand,
          ...formValues,
          invoiceIds: selectedInvoiceIds,
        },
        demandId: id,
      }),
    onSuccess: () => {
      showToast({
        text: 'Successfully updated',
        type: 'success',
      });
      refetch();
      refetchInvoices();
    },
    onError: (error) => {
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  const form = useDemandForm(
    id ? updateDemand : createDemand,
    demand,
    isLender
  );

  const cols = [
    {
      title: 'Sequence',
      keys: ['sequence'],
    },
    {
      title: 'Number',
      keys: ['pixel_invoice_number'],
    },
    {
      title: 'Date',
      keys: ['date'],
      type: 'date',
    },
    {
      title: 'Date Overdue',
      keys: ['date_overdue'],
      type: 'date',
    },
    {
      title: 'Total',
      keys: ['_total.invoice_total'],
      type: 'currency',
    },
    {
      title: 'Paid',
      keys: ['_paid.amount_paid'],
      type: 'currency',
    },
    {
      title: '',
      keys: ['doesntmatter'],
      type: 'checkbox',
      onChange: (row) => {
        setSelectedInvoiceIds((prevState) => {
          if (prevState.includes(row.id)) {
            // eslint-disable-next-line no-shadow
            return prevState.filter((id) => id !== row.id);
          }

          return [...prevState, row.id];
        });
      },
      isSelected: (row) => selectedInvoiceIds.includes(row.id),
      disabled: () => demand && demand?.status !== 'pending',
      isHidden: () => demand && demand?.status !== 'pending',
    },
  ];

  return (
    <div>
      <div className="mb-4 relative top-[0.5px] flex sm:h-[37.13px] justify-between flex-col sm:flex-row gap-4">
        <AddOrEditPageTabs tabs={tabs} />
        {isSuperAdmin &&
          demand?.status === 'active' &&
          !demand?.acceleration_date && (
            <div>
              <ButtonWithConfirmation
                confirmationMessage="Are you sure you want to generate an acceleration letter and send it to the attorney for review?"
                label="Acceleration Review"
                confirmButtonLabel="Yes, proceed"
                cancelButtonLabel="No, cancel"
                successMessage="The acceleration package has been sent to the attorney"
                onConfirm={() => DemandService.accelerateDemand(id)}
                refetch={() => {
                  refetch();
                  refetchInvoices();
                }}
              />
            </div>
          )}
        {isLender && <AccelerationPackage isLender demandId={id} />}
      </div>
      <div className="relative p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
        <div className="mb-10">
          <FormContainer
            form={form}
            groupSizeOffset={1}
            noBillComId
            isSubmitting={isPending || isUpdating}
            noSubmit={isLender}
            headerRight={
              demand?.status === 'pending' &&
              isSuperAdmin && (
                <div>
                  <button
                    disabled={isDeleting}
                    onClick={() => {
                      showDialogModal({
                        title: 'Confirmation',
                        message: 'Are you sure you want to delete this demand?',
                        type: 'danger',
                        onConfirm: () => {
                          deleteDemand();
                        },
                        confirmButtonLabel: 'Yes, delete it',
                        cancelButtonLabel: 'No, cancel',
                      });
                    }}
                    type="button"
                    className="rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <span className="sr-only">Back</span>
                    <svg width="32" height="32" viewBox="0 0 32 32">
                      <path
                        className="fill-current"
                        d="M15.95 14.536l4.242-4.243a1 1 0 111.415 1.414l-4.243 4.243 4.243 4.242a1 1 0 11-1.415 1.415l-4.242-4.243-4.243 4.243a1 1 0 01-1.414-1.415l4.243-4.242-4.243-4.243a1 1 0 011.414-1.414l4.243 4.243z"
                      />
                    </svg>
                  </button>
                </div>
              )
            }
          />
        </div>
        <Table rows={invoices} cols={cols} title="Past due invoices" />
        {id && demand?.payment_id?.length ? (
          <div className="mt-10">
            <PaymentsTable payments={demand?.payment_id} loanId={loanId} />
          </div>
        ) : null}
        {id && demand?.delivery?.length ? (
          <div className="mt-10">
            <DeliveriesTable
              deliveries={demand?.delivery}
              isActive={demand?.status === 'active'}
              demandId={id}
              refetch={refetch}
              demand={demand}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
